import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';
import { Container } from '@vnetwork/ui/layout';
import { Card, CardBody, CardHeader, CardTitle, Button } from '@vnetwork/ui/primitives';
import { StatusPill } from '@vnetwork/ui/data';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import { useBusinessDetail } from '@/api/businesses-admin';
import { dataCentralApi } from '@/api/client';
import { DetailHeader } from '@/components/detail-header';
import { InfoGrid } from '@/pages/account/components/InfoGrid';
import { statusTone } from '@/pages/account/components/status-tone';
import { TabLoading, TabError } from '@/pages/customers/detail/tab-states';
import { formatWeekdayDateTime, formatDateTime } from '@/lib/format';
import { BusinessRelatedCard, BusinessParentCard } from './businesses-admin-related-card';

const cap = (s?: string | null) =>
  s ? s.charAt(0).toUpperCase() + s.slice(1).replace(/-/g, ' ') : '—';

const TYPE_LABEL: Record<string, string> = {
  meeting: 'Meeting',
  datacenter: 'Datacenter',
  orthers: 'Others',
};

const OPTION_LABEL: Record<string, string> = {
  '0': 'Morning',
  '1': 'Afternoon',
  '2': 'Full day',
};

function errMsg(err: unknown): string {
  const r = (err as { response?: { data?: { error_description?: string; message?: string } } })
    ?.response;
  return r?.data?.error_description ?? r?.data?.message ?? 'Something went wrong.';
}

/**
 * HRM Business detail — breadcrumb + status pills + leader/BOD approve actions,
 * trip-info card, days breakdown, optional customer card, parent and related trips.
 * Mirrors legacy BusinessInfo + RelatedBusinesses (detail, pageType="hrm").
 */
export default function BusinessesAdminDetailPage() {
  const { id: idParam } = useParams();
  const id = Number(idParam) || 0;
  const toast = useToast();

  const [leaderOpen, setLeaderOpen] = useState(false);
  const [bodOpen, setBodOpen] = useState(false);
  const [approving, setApproving] = useState(false);

  const { data, isLoading, isError, refetch } = useBusinessDetail(id);
  const business = data?.business;
  const parentBusiness = data?.parent_business;

  async function handleLeaderApprove() {
    setApproving(true);
    try {
      await dataCentralApi.put(`/businesses/${id}/lead/approve`, { business_leader_feedback: '' });
      toast.success('Leader approved');
      refetch();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setApproving(false);
      setLeaderOpen(false);
    }
  }

  async function handleBodApprove() {
    setApproving(true);
    try {
      await dataCentralApi.put(`/businesses/${id}/bod/approve`, { business_bod_feedback: '' });
      toast.success('BOD approved');
      refetch();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setApproving(false);
      setBodOpen(false);
    }
  }

  if (isError) {
    return (
      <Container size="full">
        <TabError title="Failed to load business trip" onRetry={refetch} />
      </Container>
    );
  }

  if (isLoading || !business) {
    return (
      <Container size="full">
        <TabLoading rows={8} />
      </Container>
    );
  }

  const canLeader =
    business.business_leader_status === 'pending' || business.business_status === 'pending';
  const canBod =
    business.business_leader_status !== 'pending' && business.business_bod_status === 'pending';
  const days = business.metadata?.days ?? [];
  const customer = business.metadata?.customer;

  return (
    <Container size="full">
      <DetailHeader
        parent={{ label: 'Business Trips', to: '/businesses' }}
        current={`IBN-${business.business_id}`}
        idPrefix={`IBN-${business.business_id}`}
        title={business.employee_name}
        meta={
          <>
            <StatusPill tone={statusTone(business.business_status)}>
              {cap(business.business_status)}
            </StatusPill>
            <StatusPill tone={statusTone(business.business_leader_status)}>
              Leader: {cap(business.business_leader_status)}
            </StatusPill>
            <StatusPill tone={statusTone(business.business_bod_status)}>
              BOD: {cap(business.business_bod_status)}
            </StatusPill>
          </>
        }
        actions={
          <div style={{ display: 'flex', gap: 'var(--vn-space-2)' }}>
            {canLeader && (
              <Button size="sm" variant="secondary" startIcon={<CheckCircle size={14} />} onClick={() => setLeaderOpen(true)}>
                Leader approve
              </Button>
            )}
            {canBod && (
              <Button size="sm" variant="secondary" startIcon={<CheckCircle size={14} />} onClick={() => setBodOpen(true)}>
                BOD approve
              </Button>
            )}
          </div>
        }
      />

      <div style={{ display: 'grid', gap: 'var(--vn-space-4)', marginTop: 'var(--vn-space-4)', gridTemplateColumns: 'minmax(0, 1fr)' }}>
        {/* Trip info */}
        <Card>
          <CardHeader><CardTitle>Trip information</CardTitle></CardHeader>
          <CardBody>
            <InfoGrid items={[
              { label: 'Employee', value: business.employee_name },
              { label: 'Department', value: business.department_name },
              { label: 'Position', value: business.position_name },
              { label: 'Type', value: TYPE_LABEL[business.business_type] ?? cap(business.business_type) },
              { label: 'Start', value: formatWeekdayDateTime(business.business_startdate) },
              { label: 'End', value: formatWeekdayDateTime(business.business_enddate) },
              { label: 'Location', value: business.business_place || '—' },
              { label: 'Created', value: formatDateTime(business.business_created_at) },
              { label: 'Content', value: business.business_content, full: true },
              ...(business.business_reason_cancelled ? [{ label: 'Cancel reason', value: business.business_reason_cancelled, full: true as const }] : []),
              ...(business.business_leader_feedback ? [{ label: 'Leader feedback', value: business.business_leader_feedback, full: true as const }] : []),
              ...(business.business_bod_feedback ? [{ label: 'BOD feedback', value: business.business_bod_feedback, full: true as const }] : []),
            ]} />
          </CardBody>
        </Card>

        {/* Days breakdown */}
        {days.length > 0 && (
          <Card>
            <CardHeader><CardTitle>Days breakdown</CardTitle></CardHeader>
            <CardBody>
              <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
                {days.map((day, i) => (
                  <div key={i} style={{ display: 'flex', gap: 'var(--vn-space-3)', alignItems: 'center', padding: 'var(--vn-space-2) var(--vn-space-3)', background: 'var(--vn-bg-subtle)', borderRadius: 'var(--vn-radius-sm)', fontSize: 'var(--vn-font-size-sm)' }}>
                    <span style={{ color: 'var(--vn-fg-muted)', minWidth: 80 }}>
                      {OPTION_LABEL[day.business_option] ?? day.business_option}
                    </span>
                    <span>
                      {formatWeekdayDateTime(day.business_startdate)}
                      {' – '}
                      {formatWeekdayDateTime(day.business_enddate)}
                    </span>
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>
        )}

        {/* Customer (meeting type) */}
        {customer && customer.customer_id > 0 && (
          <Card>
            <CardHeader><CardTitle>Customer</CardTitle></CardHeader>
            <CardBody>
              <InfoGrid items={[
                { label: 'Name', value: customer.customer_name },
                { label: 'Company', value: customer.company_name ?? '—' },
                { label: 'Phone', value: customer.customer_phone ?? '—' },
                { label: 'Email', value: customer.customer_email ?? '—' },
              ]} />
            </CardBody>
          </Card>
        )}

        {parentBusiness && <BusinessParentCard parent={parentBusiness} />}
        <BusinessRelatedCard businessId={id} />
      </div>

      <ConfirmDialog
        open={leaderOpen}
        onOpenChange={(o) => { if (!o) setLeaderOpen(false); }}
        title="Leader approve"
        description={`Approve IBN-${id} — ${business.employee_name}?`}
        confirmLabel="Approve"
        loading={approving}
        onConfirm={handleLeaderApprove}
      />
      <ConfirmDialog
        open={bodOpen}
        onOpenChange={(o) => { if (!o) setBodOpen(false); }}
        title="BOD approve"
        description={`Approve IBN-${id} — ${business.employee_name}?`}
        confirmLabel="Approve"
        loading={approving}
        onConfirm={handleBodApprove}
      />
    </Container>
  );
}
